import { AsyncPipe } from '@angular/common';
import { Component, computed, inject, OnInit, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Data, Router, RouterLink } from '@angular/router';
import {
  ConfirmModalComponent,
  FriendlyCaptchaComponent,
  InlineInfoComponent,
  SalaryInputComponent,
  TagComponent,
} from '@catch/general-ui';
import { FileInputComponent } from '@catch/file-input';
import { ImageEditorComponent } from '@catch/image-editor';
import { TranslocoPipe } from '@jsverse/transloco';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgSelectModule } from '@ng-select/ng-select';
import { ImageCropperComponent } from 'ngx-image-cropper';
import { AutosizeModule } from 'ngx-autosize';
import { HeaderTagsComponent } from '../../snippets/header-tags/header-tags.component';
import { DateInputComponent } from '../../snippets/date-input/date-input.component';
import { SkillTestComponent } from '../../snippets/skill-test/skill-test.component';
import { NoSpacesDirective } from '../../../directives/no-spaces.directive';
import { PrivacyModalComponent } from '../../snippets/privacy-modal/privacy-modal.component';
import { AnimatedCheckComponent } from '../../snippets/animated-check/animated-check.component';
import { AnimatedCrossComponent } from '../../snippets/animated-cross/animated-cross.component';
import { Constants, ConstantsService, ToastsService } from '@catch/services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { serialize } from 'object-to-formdata';
import { firstValueFrom, Observable, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { collapse } from '../../../../assets/animations/animations';
import { emailRegex, nameRegex } from '../../../../assets/regex';
import { JobboardAdapter } from '../../../adapters/jobboard.adapter';
import { applicantUrlPages } from '../../../constants/applicantUrlPages';
import { fileTypes } from '../../../constants/filetypes';
import { AcademicTitle } from '../../../enums/academic-title.enum';
import { ApplicantSkillLevel } from '../../../enums/applicant-skill-level.enum';
import { Gender } from '../../../enums/gender.enum';
import { LanguageLevelCode } from '../../../enums/language-level-code.enum';
import { SalaryInterval } from '../../../enums/salary-interval.enum';
import { SkillType } from '../../../enums/skill-type.enum';
import { WorkingModel } from '../../../enums/working-model.enum';
import { CompanyInfo } from '../../../interfaces/company-info';
import { Facility } from '../../../interfaces/facility';
import { Job } from '../../../interfaces/job';
import { JobDetail } from '../../../interfaces/job-detail';
import { MissingDataFields } from '../../../interfaces/missing-data-fields';
import { Skill } from '../../../interfaces/skill';
import { ColorService } from '../../../services/color.service';
import { EmbedService } from '../../../services/embed.service';
import { FontawesomeService } from '../../../services/fontawesome.service';
import { RefService } from '../../../services/ref.service';
import { SEOService } from '../../../services/seo.service';
import { identify as identifyDash0 } from '@dash0/sdk-web';

@UntilDestroy()
@Component({
  selector: 'app-apply',
  templateUrl: './apply.component.html',
  styleUrls: ['./apply.component.scss'],
  animations: [collapse],
  imports: [
    AsyncPipe,
    TranslocoPipe,
    RouterLink,
    ReactiveFormsModule,
    FormsModule,
    NgSelectModule,
    NoSpacesDirective,
    AutosizeModule,
    ConfirmModalComponent,
    FriendlyCaptchaComponent,
    InlineInfoComponent,
    HeaderTagsComponent,
    DateInputComponent,
    SkillTestComponent,
    TagComponent,
    FaIconComponent,
    FileInputComponent,
    ImageEditorComponent,
    SalaryInputComponent,
    PrivacyModalComponent,
    AnimatedCheckComponent,
    AnimatedCrossComponent,
    ImageCropperComponent
],
})
export class ApplyComponent implements OnInit {
  @ViewChild('imageModal') imageModal: ConfirmModalComponent;
  @ViewChild('navigationConfirmModal')
  navigationConfirmModal: ConfirmModalComponent;
  @ViewChild(FriendlyCaptchaComponent) captcha: FriendlyCaptchaComponent;

  private readonly TOTAL_FORM_SIZE: number = 64000000;

  companyInformation$: Observable<CompanyInfo>;
  job$: Observable<JobDetail>;
  companyJobs$: Observable<Array<Job>>;
  facilities$: Observable<Array<Facility>>;
  missingDataFields$: Observable<MissingDataFields>;

  type: string;
  applyForm: FormGroup;
  applyFormSubmitAttempt: boolean = false;
  selectedSkillLevels: {
    id: number;
    levelID: ApplicantSkillLevel;
    comparable: boolean;
  }[] = [];
  selectedLanguageLevels: { id: string; levelID: LanguageLevelCode }[] = [];

  termsChecked: boolean = false;
  termsChecked2: boolean = false;
  success: boolean = false;
  sending: boolean = false;
  sendingCv: boolean = false;
  missingName: boolean = false;
  missingTitle: boolean = false;
  missingAcademicTitle: boolean = false;
  noMissingData: boolean = false;
  matchingLanguage = null;
  navigationAllowed: boolean = false;
  errorGettingMissingData: boolean = false;

  datePickerConfig = {
    format: 'DD.MM.YYYY',
    firstDayOfWeek: 'mo',
    locale: 'de',
    min: new Date().getUTCDate().toString(),
  };

  fileTypes = fileTypes;

  SkillType = SkillType;
  WorkingModel = WorkingModel;
  SalaryInterval = SalaryInterval;
  Gender = Gender;
  AcademicTitle = AcademicTitle;

  private readonly colorService = inject(ColorService);
  private readonly fb = inject(FormBuilder);
  private readonly jobboardAdapter = inject(JobboardAdapter);
  private readonly refService = inject(RefService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly seoService = inject(SEOService);
  private readonly toastsService = inject(ToastsService);
  public readonly constantsService = inject(ConstantsService);
  public readonly embedService = inject(EmbedService);
  public readonly fontawesomeService = inject(FontawesomeService);

  readonly job = toSignal(
    this.route.data.pipe(map((data: Data) => data['job'] as JobDetail)),
    { initialValue: undefined as JobDetail | undefined }
  );

  readonly missingDataFields = toSignal(
    this.route.data.pipe(
      map((data: Data) => data['missingData'] as MissingDataFields)
    ),
    { initialValue: undefined as MissingDataFields | undefined }
  );

  readonly missingDataFieldsGeneral = computed(() => {
    const missingDataFields = this.missingDataFields();
    return !!(
      missingDataFields?.phone ||
      missingDataFields?.salary ||
      missingDataFields?.startDate ||
      missingDataFields?.applicantData?.firstName === null ||
      missingDataFields?.applicantData?.lastName === null ||
      missingDataFields?.applicantData?.title === null
    );
  });

  readonly missingDataFieldsDocuments = computed(() => {
    const missingDataFields = this.missingDataFields();
    return !!(
      missingDataFields?.cv ||
      missingDataFields?.coverLetter ||
      missingDataFields?.certificates ||
      missingDataFields?.others ||
      missingDataFields?.image
    );
  });

  readonly fileInputCount = computed(() => {
    if (this.initiative) {
      return 4;
    }
    const job = this.job();
    const missingDataFields = this.missingDataFields();
    let count = 0;
    if (this.isFileFieldVisible('cv', job, missingDataFields)) count++;
    if (this.isFileFieldVisible('coverLetter', job, missingDataFields)) count++;
    if (this.isFileFieldVisible('certificates', job, missingDataFields)) count++;
    if (this.isFileFieldVisible('others', job, missingDataFields)) count++;
    return count;
  });

  readonly fileInputCols = computed(() => {
    const count = this.fileInputCount();
    if (count <= 0) {
      return 'col-12 mt-3';
    }
    return `col-6 col-md-${12 / count} mt-3`;
  });

  constructor() {
    this.type = this.route.snapshot.data['type'];
  }

  ngOnInit(): void {
    this.applyForm = this.fb.group(
      {
        title: [null],
        academicTitle: [null],
        firstName: [null, Validators.pattern(nameRegex)],
        lastName: [null, Validators.pattern(nameRegex)],
        email: [null, [Validators.email, Validators.pattern(emailRegex)]],
        emailConfirm: [
          null,
          [Validators.email, Validators.pattern(emailRegex)],
        ],
        phonePrefix: ['+49'],
        phone: [null, Validators.pattern(/^[+0-9 -]*$/)],
        startDate: [null],
        salary: [null, Validators.max(20000000)],
        urls: this.fb.array([]),
        languages: this.fb.array([]),
        skills: this.fb.array([]),
        comparableSkills: this.fb.array([]),
        isa: [[]],
        questions: this.fb.array([]),
        workLocations: [[]],
        manualApplication: [this.manualApplication],
        image: [null],
        cv: [[]],
        coverLetter: [[]],
        certificates: [[]],
        others: [[]],
        jobTitle: [null],
        captchaCode: [null],
      },
      {
        validators: [
          this.confirmField('email', 'emailConfirm', 'emailMismatch'),
        ],
      }
    );

    this.job$ = this.route.data.pipe(map((data: Data) => data['job']));
    this.companyInformation$ = this.route.data.pipe(
      map((data: Data) => data['companyInfo'])
    );
    this.companyJobs$ = this.route.data.pipe(map((data: Data) => data['jobs']));
    this.missingDataFields$ = this.route.data.pipe(
      map((data: Data) => data['missingData'])
    );

    this.companyInformation$
      .pipe(
        tap((companyInfo: CompanyInfo) => {
          this.colorService.setDesign(this.route.snapshot.params['companyId']);
          this.seoService.setWebsite(companyInfo.website);
          if (this.initiative && companyInfo.ISA) {
            this.isa.addValidators(Validators.required);
            this.updateFormValidity();
          }
        }),
        switchMap((companyInfo: CompanyInfo) =>
          this.initiative
            ? this.seoService.updateUnsolicitedApplyTitle(companyInfo.title)
            : of(undefined)
        ),
        untilDestroyed(this)
      )
      .subscribe();

    this.phonePrefix.patchValue('+49');

    this.setValidators();
  }

  setValidators() {
    const startsWith = (start: string): ValidatorFn => {
      return (control: AbstractControl): ValidationErrors | null => {
        if (!control?.value) return null;
        return ((control?.value as string) ?? '').startsWith(start)
          ? null
          : { start: { value: control.value } };
      };
    };

    if (!this.missingData) {
      this.captchaCode.addValidators(Validators.required);
      this.firstName.addValidators(Validators.required);
      this.lastName.addValidators(Validators.required);
      this.email.addValidators(Validators.required);
      this.emailConfirm.addValidators(Validators.required);
      for (const value of applicantUrlPages) {
        this.urls.push(new FormControl(null, startsWith(value[1])));
      }
    } else {
      this.firstName.clearValidators();
      this.lastName.clearValidators();
      this.email.clearValidators();
      this.emailConfirm.clearValidators();
      this.phone.clearValidators();
      this.salary.clearValidators();
    }

    if (this.initiative) {
      this.updateFormValidity();
      return;
    }

    this.job$
      .pipe(
        filter((job): job is JobDetail => !!job),
        switchMap((job) => this.seoService.updateJobPageSeo(job)),
        untilDestroyed(this)
      )
      .subscribe();

    this.job$.pipe(untilDestroyed(this)).subscribe({
      next: (job: JobDetail) => {
        if (!job) return;
        this.refService.countJobClick(job.companyID, job.id);
        if (!this.missingData) {
          if (
            job.workLocations.length === 1 &&
            (job.workingModel === WorkingModel.ON_SITE ||
              job.workingModel === WorkingModel.HYBRID)
          )
            this.workLocations.patchValue([job.workLocations[0].id]);
        }

        if (this.normalApplication) {
          if (job.fields.certificates.required)
            this.certificates.addValidators(Validators.required);
          if (job.fields.coverLetter.required)
            this.coverLetter.addValidators(Validators.required);
          if (job.fields.cv.required)
            this.cv.addValidators(Validators.required);
          if (job.fields.phone.required) {
            this.phonePrefix.addValidators(Validators.required);
            this.phone.addValidators(Validators.required);
          }
          if (job.fields.salary.required)
            this.salary.addValidators(Validators.required);
          if (job.fields.startDate.required)
            this.startDate.addValidators(Validators.required);
          if (job.isaEnabled) this.isa.addValidators(Validators.required);

          job.questions.forEach((question) =>
            this.questions.push(
              new FormControl(
                null,
                question.required ? Validators.required : undefined
              )
            )
          );
          job.skills.forEach(() => {
            this.skills.push(new FormControl(null, Validators.required));
            this.comparableSkills.push(new FormControl(null));
          });
          job.languages.forEach(() =>
            this.languages.push(new FormControl(null, Validators.required))
          );
        } else if (this.missingData) {
          this.missingDataFields$.subscribe({
            next: (missingDataFields: MissingDataFields) => {
              if (!missingDataFields) {
                this.errorGettingMissingData = true;
                this.deleteJobNonce();
                return;
              }

              this.noMissingData = !this.hasAnyMissingData(missingDataFields);
              if (this.noMissingData) {
                this.deleteJobNonce();
                this.success = true;
                return;
              }

              this.missingName =
                missingDataFields.applicantData?.firstName === null ||
                missingDataFields.applicantData?.lastName === null
                  ? true
                  : false;
              this.missingTitle =
                missingDataFields.applicantData?.title === null ? true : false;
              this.missingAcademicTitle =
                missingDataFields.applicantData?.academicTitle === null
                  ? true
                  : false;

              this.title.setValue(missingDataFields.applicantData?.title);
              this.firstName.setValue(
                missingDataFields.applicantData?.firstName
              );
              this.lastName.setValue(missingDataFields.applicantData?.lastName);
              if (this.urls.length > 0) {
                for (const url of missingDataFields.applicantData.urls) {
                  this.urls
                    .at(applicantUrlPages.findIndex((el) => el[0] === url.page))
                    .setValue(url.url);
                }
              }

              (missingDataFields.skills ?? []).forEach(() => {
                this.skills.push(new FormControl());
                this.comparableSkills.push(new FormControl());
              });
              (missingDataFields.languages ?? []).forEach(() =>
                this.languages.push(new FormControl())
              );
              (missingDataFields.questions ?? []).forEach(() =>
                this.questions.push(new FormControl())
              );
              this.job$ = this.job$.pipe(
                tap((job) => (job.skills = missingDataFields.skills ?? [])),
                tap(
                  (job) => (job.languages = missingDataFields.languages ?? [])
                ),
                tap(
                  (job) => (job.questions = missingDataFields.questions ?? [])
                )
              );
              this.jobboardAdapter
                .trackMissingDataClick(
                  this.route.snapshot.params['applicationId'],
                  this.route.snapshot.queryParams['utm_source']
                )
                .subscribe();
            },
          });
        }
      },
    });
    this.updateFormValidity();
  }

  private hasAnyMissingData(missingDataFields: MissingDataFields): boolean {
    if (!missingDataFields) return false;

    const hasMissingGeneralFields =
      !!missingDataFields.phone ||
      !!missingDataFields.salary ||
      !!missingDataFields.startDate ||
      missingDataFields.applicantData?.firstName === null ||
      missingDataFields.applicantData?.lastName === null ||
      missingDataFields.applicantData?.title === null ||
      missingDataFields.applicantData?.academicTitle === null;

    const hasMissingDocuments =
      !!missingDataFields.cv ||
      !!missingDataFields.coverLetter ||
      !!missingDataFields.certificates ||
      !!missingDataFields.others ||
      !!missingDataFields.image;

    const hasMissingLists =
      (missingDataFields.languages?.length ?? 0) > 0 ||
      (missingDataFields.skills?.length ?? 0) > 0 ||
      (missingDataFields.questions?.length ?? 0) > 0;

    return hasMissingGeneralFields || hasMissingDocuments || hasMissingLists || !!missingDataFields.isa;
  }

  selectSkillLevel(event, index) {
    this.job$
      .pipe(
        map((job) => job.skills[index]),
        untilDestroyed(this)
      )
      .subscribe({
        next: (skill: Skill) => {
          if (event === undefined) {
            this.selectedSkillLevels.splice(
              this.selectedSkillLevels.indexOf(
                this.selectedSkillLevels.find(
                  (selectedSkill) => selectedSkill.id === skill.id
                )
              ),
              1
            );
            return;
          }

          const selectedSkill = this.selectedSkillLevels.find(
            (selectedSkill) => selectedSkill.id === skill.id
          );
          if (selectedSkill && event === 'comparable') {
            this.selectedSkillLevels.splice(
              this.selectedSkillLevels.indexOf(selectedSkill),
              1
            );
            this.comparableSkills[index].reset();
          }
          if (event === 'comparable') return;
          if (selectedSkill) {
            selectedSkill.comparable = event.toString().includes('comparable-');
            selectedSkill.levelID = event
              .toString()
              .replace('comparable-', '') as ApplicantSkillLevel;
          } else {
            this.selectedSkillLevels.push({
              id: skill.id,
              levelID: event
                .toString()
                .replace('comparable-', '') as ApplicantSkillLevel,
              comparable: event.toString().includes('comparable-'),
            });
          }
        },
      });
  }

  selectLanguageLevel(event, index) {
    this.job$
      .pipe(
        map((job) => job.languages[index]),
        untilDestroyed(this)
      )
      .subscribe({
        next: (id: string) => {
          if (event === undefined) {
            this.selectedLanguageLevels.splice(
              this.selectedLanguageLevels.findIndex(
                (selectedLanguage) => selectedLanguage.id === id
              ),
              1
            );
            return;
          }
          const selectedLanguage = this.selectedLanguageLevels.find(
            (selectedLanguage) => selectedLanguage.id === id
          );
          if (selectedLanguage) {
            selectedLanguage.levelID = event;
          } else if (event !== undefined) {
            this.selectedLanguageLevels.push({
              id,
              levelID: event,
            });
          }
        },
      });
  }

  getFullPayloadSize(payload: object): number {
    let size = 0;
    const iterate = (array: Array<any>) => {
      array.forEach((value) => {
        if (typeof value === 'string') {
          size += new Blob([value]).size;
        } else if (value instanceof File) {
          size += value.size;
        } else if (value instanceof Array) {
          iterate(value);
        }
      });
    };
    iterate(Object.values(payload));
    return size;
  }

  countFiles(payload: object): number {
    let count = 0;
    const iterate = (array: Array<any>) => {
      array.forEach((value) => {
        if (value instanceof File) {
          count++;
        } else if (value instanceof Array) {
          iterate(value);
        }
      });
    };
    iterate(Object.values(payload));
    return count;
  }

  /*onApplyCvPrefill() {
    const formData = new FormData();
    formData.append('cv', this.cv.value);
    formData.append('outputLanguage', 'de')
    this.sendingCv = true;

    this.jobboardAdapter.getPrefillDataFromCv(formData).pipe(untilDestroyed(this)).subscribe({
      next: (cvData: CvData) => {
        const firstNameCv = cvData.data.answer.general.applicantName.split(" ")[0]
        const lastNameCv = cvData.data.answer.general.applicantName.split(" ")[1]
        this.firstName.patchValue(firstNameCv);
        this.lastName.patchValue(lastNameCv);
        this.phone.patchValue(cvData.data.answer.general.applicantPhone);

        this.job$.pipe(untilDestroyed(this)).subscribe({
          next: (job: JobDetail) => {
            job.languages.forEach(jobLanguage => {
              this.matchingLanguage = cvData.data.answer.content.languageSkills.find(cvLanguage => cvLanguage.language === jobLanguage);
              if(
                this.matchingLanguage !== null &&
                this.LANGUAGE_LEVELS.includes(this.matchingLanguage.level)
              ) {
                const control = this.languages.controls[job.languages.indexOf(jobLanguage)]
                if (control) {
                  control.patchValue(this.matchingLanguage.level);
                }
              }
            });
          },
        });
        this.sendingCv = false;
      },
      error: () => {
        this.sendingCv = false;
      },
    });
  }*/

  async onApplyFormSubmit() {
    if (this.sending || this.success) return;

    identifyDash0(this.route.snapshot.params['applicationId'] ?? this.email.value ?? 'unknown', {
      name: this.firstName.value,
      fullName: this.firstName.value + ' ' + this.lastName.value,
      email: this.email.value,
    });

    this.applyFormSubmitAttempt = true;
    if (this.title.value === null && !this.missingData)
      this.title.patchValue(Gender.DIVERSE);
    if (this.academicTitle.value === null && !this.missingData)
      this.academicTitle.patchValue(AcademicTitle.NONE);
    if (Number.isNaN(this.startDate.value)) this.startDate.patchValue(null);

    if (!this.applyForm.valid) {
      this.getFormValidationErrors();
      setTimeout(() => {
        this.toastsService.warning(
          'jobboard.views.apply.sections.finish.missing-detail'
        );
        if (document.querySelectorAll('.invalid')[0]) {
          document.querySelectorAll('.invalid')[0].scrollIntoView();
        }
      }, 100);
      return;
    }

    this.sending = true;
    const payload = this.applyForm.value;
    delete payload.emailConfirm;
    delete payload.phonePrefix;
    delete payload.urls;
    payload.languages = JSON.stringify(
      this.selectedLanguageLevels.filter(
        (el) => el.levelID !== null && el.levelID !== undefined
      )
    );
    payload.skills = JSON.stringify(
      this.selectedSkillLevels.filter(
        (el) => el.levelID !== null && el.levelID !== undefined
      )
    );
    payload.isa = JSON.stringify(this.isa.value);
    payload.termsChecked = JSON.stringify(this.termsChecked2);
    const job = await firstValueFrom(this.job$);
    if (job) {
      payload.questions = this.questions.controls
        .filter((control) => control.value)
        .map((control, i) => {
          return {
            id: job.questions[i].id,
            answer: control.value,
          };
        });
      payload.questions = JSON.stringify(payload.questions);
      payload.urls = JSON.stringify(
        this.urls.controls
          .map((control, index) => ({
            page: applicantUrlPages[index][0],
            control,
          }))
          .filter((el) => el.control.dirty)
          .map((el) => ({ page: el.page, url: el.control.value }))
      );
    }
    if (this.initiative) {
      payload.questions = JSON.stringify([]);
      payload.urls = JSON.stringify([]);
    }

    const phoneMatch = payload.phone?.match(
      /(((\+|00)[0-9]{0,2})|(0))?([0-9]*)/
    );
    if (phoneMatch && !phoneMatch[2] && this.phonePrefix.value) {
      payload.phone = this.phonePrefix.value + phoneMatch[5];
    }

    payload.utm_source = this.manualApplication
      ? 'manual-form'
      : this.refService.utm_source;
    payload.utm_campaign = this.refService.utm_campaign;
    //payload.utm_content = this.refService.utm_content;

    if (this.missingData) {
      payload.nonce = this.route.snapshot.queryParams['nonce'];
      payload.missingDataTrigger =
        this.route.snapshot.data['trigger'] ||
        this.route.snapshot.params['trigger'];
      if (this.firstName.pristine) delete payload.firstName;
      if (this.lastName.pristine) delete payload.lastName;
      if (this.title.pristine) delete payload.title;
      if (this.academicTitle.pristine) delete payload.academicTitle;
    }

    const formDataPayload: FormData = serialize(payload, {
      nullsAsUndefineds: true,
    });

    let emptyPayload = true;
    formDataPayload.forEach((value, key) => {
      if (key !== 'manualApplication' && key !== 'nonce') {
        emptyPayload = value === undefined ? emptyPayload : false;
      }
    });
    if (emptyPayload) {
      this.toastsService.warning('jobboard.views.apply.empty-form-submit');
      this.sending = false;
      return;
    }

    if (this.getFullPayloadSize(payload) > this.TOTAL_FORM_SIZE) {
      this.toastsService.warning(
        'jobboard.views.apply.total-form-size-warning'
      );
      this.sending = false;
      return;
    }

    if (this.countFiles(payload) > 20) {
      this.toastsService.warning(
        'jobboard.views.apply.total-form-count-warning'
      );
      this.sending = false;
      return;
    }

    const resetAfterSuccess = () => {
      this.applyForm.reset();
      this.success = true;
      this.sending = false;
    };

    if (this.initiative) {
      this.jobboardAdapter
        .applyInitiative(
          this.route.snapshot.params['companyId'],
          formDataPayload
        )
        .pipe(untilDestroyed(this))
        .subscribe({
          next: () => resetAfterSuccess(),
          error: () => {
            this.captcha?.setWidget();
            this.sending = false;
          },
        });
    } else if (this.missingData) {
      this.jobboardAdapter
        .setMissingData(
          this.route.snapshot.params['applicationId'],
          formDataPayload
        )
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (result) => {
            if (result?.['complete']) this.deleteJobNonce();
            resetAfterSuccess();
          },
          error: () => {
            this.captcha?.setWidget();
            this.sending = false;
          },
        });
    } else {
      if (this.route.snapshot.queryParams['recommendationNonce']) {
        formDataPayload.set(
          'recommendationNonce',
          this.route.snapshot.queryParams['recommendationNonce']
        );
        formDataPayload.set('utm_source', 'manual-talenthub-recommendation');
      }
      this.jobboardAdapter
        .applyToJob(this.route.snapshot.params['jobId'], formDataPayload)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: () => resetAfterSuccess(),
          error: () => {
            this.captcha?.setWidget();
            this.sending = false;
          },
        });
    }
  }

  getFormValidationErrors() {
    Object.keys(this.applyForm.controls).forEach((key) => {
      const controlErrors: ValidationErrors = this.applyForm.get(key).errors;
      if (controlErrors !== null && controlErrors !== undefined) {
        Object.keys(controlErrors).forEach((keyError) => {
          console.log(
            'Control: ' +
              key +
              ', Validation Error: ' +
              keyError +
              ', Control Value: ' +
              this.applyForm.controls[key].value
          );
        });
      }
    });
  }

  updateFormValidity() {
    Object.values(this.applyForm.controls).forEach((control) => {
      control.updateValueAndValidity();
    });
  }

  private isFileFieldVisible(
    field: 'cv' | 'coverLetter' | 'certificates' | 'others',
    job?: JobDetail,
    missingDataFields?: MissingDataFields
  ): boolean {
    const showByJob = !!job?.fields[field]?.show && !this.missingData;
    return (
      showByJob ||
      this.initiative ||
      this.manualApplication ||
      !!missingDataFields?.[field]
    );
  }

  get initiative() {
    return this.type === 'initiative' || this.type === 'manual-initiative';
  }

  get manualApplication() {
    return (
      this.type === 'manual-application' || this.type === 'manual-initiative'
    );
  }

  get manualInitiative() {
    return this.type === 'manual-initiative';
  }

  get missingData() {
    return this.type === 'missing-data';
  }

  get normalApplication() {
    return !this.type;
  }

  get title() {
    return this.applyForm.get('title');
  }

  get academicTitle() {
    return this.applyForm.get('academicTitle');
  }

  get firstName() {
    return this.applyForm.get('firstName');
  }

  get lastName() {
    return this.applyForm.get('lastName');
  }

  get email() {
    return this.applyForm.get('email');
  }

  get emailConfirm() {
    return this.applyForm.get('emailConfirm');
  }

  get phone() {
    return this.applyForm.get('phone');
  }

  get phonePrefix() {
    return this.applyForm.get('phonePrefix');
  }

  get manualApplicationControl() {
    return this.applyForm.get('manualApplication');
  }

  get jobTitle() {
    return this.applyForm.get('jobTitle');
  }

  get salary() {
    return this.applyForm.get('salary');
  }

  get workLocations() {
    return this.applyForm.get('workLocations');
  }

  get startDate() {
    return this.applyForm.get('startDate');
  }

  get isa() {
    return this.applyForm.get('isa');
  }

  get image() {
    return this.applyForm.get('image');
  }

  get cv() {
    return this.applyForm.get('cv');
  }

  get coverLetter() {
    return this.applyForm.get('coverLetter');
  }

  get certificates() {
    return this.applyForm.get('certificates');
  }

  get others() {
    return this.applyForm.get('others');
  }

  get urls() {
    return this.applyForm.get('urls') as FormArray;
  }

  get questions() {
    return this.applyForm.get('questions') as FormArray;
  }

  get skills() {
    return this.applyForm.get('skills') as FormArray;
  }

  get comparableSkills() {
    return this.applyForm.get('comparableSkills') as FormArray;
  }

  get languages() {
    return this.applyForm.get('languages') as FormArray;
  }

  get captchaId() {
    return this.applyForm.get('captchaId');
  }

  get captchaCode() {
    return this.applyForm.get('captchaCode');
  }

  get name() {
    return this.applyForm.get('name');
  }

  getPageForIndex(i: number) {
    return applicantUrlPages[i][0];
  }

  confirmField =
    (field1: string, field2: string, errorName: string) =>
    (control: AbstractControl) => {
      const email = control.get(field1);
      const emailConfirm = control.get(field2);
      return email.value &&
        emailConfirm.value &&
        email.value !== emailConfirm.value
        ? { [errorName]: true }
        : null;
    };

  deleteJobNonce() {
    const nonceMap: {
      [key: number]: {
        nonce: string;
        application: number;
        trigger?: string;
      };
    } = JSON.parse(sessionStorage.getItem('missing-data-nonces')) ?? {};
    delete nonceMap[+this.route.snapshot.params['jobId']];
    sessionStorage.setItem('missing-data-nonces', JSON.stringify(nonceMap));
  }

  private get allLabels() {
    return this.constantsService.skillLevelConstants;
  }

  get languageLevelLabels() {
    const labels = { ...this.constantsService.languageLevelConstants };
    delete labels['NK'];
    return this.constantsService.getLabelValueArray(labels).slice();
  }

  get skillLevelLabels() {
    const labels = { ...this.allLabels };
    delete labels['NOT_EXISTING'];
    delete labels['EXISTING'];
    return this.constantsService.getLabelValueArray(labels).slice().reverse();
  }

  get skillExistingLabels() {
    const labels: Constants = {
      NOT_EXISTING: this.allLabels['NOT_EXISTING'],
      EXISTING: this.allLabels['EXISTING'],
    };
    return this.constantsService.getLabelValueArray(labels).slice().reverse();
  }
}
